import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { usePRD3010_R01Store } from "./Store_PRD3010_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultPRD3010_R01LotDto, PRD3010_R01LotDto, PRD3010_R01InspectDto, PRD3010_R01DetailDto, defaultPRD3010_R01HeaderDto } from "./Dto_PRD3010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { DetailTable_PRD3010_R01 } from "./DetailTable_PRD3010_R01";

const COLUMN_STATE_KEY = 'PRD3010_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<PRD3010_R01LotDto> | null>;
	defaultPRD3010_R01LotRow: PRD3010_R01LotDto;
}

export const LotTable_PRD3010_R01 = (props: Props) => {

	const { lotGridRef, defaultPRD3010_R01LotRow } = props;

	const { 
			PRD3010_R01LotList, setPRD3010_R01LotList, 
			editPRD3010_R01, setEditPRD3010_R01,
			selectedPRD3010_R01LotList, setSelectedPRD3010_R01LotList, removeSelectedPRD3010_R01LotList, addSelectedPRD3010_R01LotList, initSelectedPRD3010_R01LotList,
			deletedPRD3010_R01LotList, setDeletedPRD3010_R01LotList, addDeletedPRD3010_R01LotList, removeDeletedPRD3010_R01LotList,
			insertPRD3010_R01LotList, updatePRD3010_R01LotList, removePRD3010_R01LotList,
			lotColumnState, setLotColumnState,
			originalPRD3010_R01LotList, setOriginalPRD3010_R01LotList,
			modalVisible, setModalVisible,
			lotScrollTop, setLotScrollTop,
		} = usePRD3010_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData: PRD3010_R01LotList,
		defaultGridData: defaultPRD3010_R01LotRow,
		selectedRow: selectedPRD3010_R01LotList,
		originalGridData: originalPRD3010_R01LotList,
		updateRow: (index: number, data: any) => updatePRD3010_R01LotList(index, data),
	});
	
	

	const headerNames: Record<string, string> = {
		rowIndex: "",
		lotQty: '생산량',
		lotCd: 'LOT No',
	}

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['lotNo'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['lotQty'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<PRD3010_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedPRD3010_R01LotList,
            deletedList: deletedPRD3010_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedPRD3010_R01LotList, deletedPRD3010_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
    ]);

	const commonInputColumns = ['lotNo', 'lotQty', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotNo', 'lotQty', 'remark'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
	InsertRowHandler({
			event: e,
			dataList: PRD3010_R01LotList,
			rowData: PRD3010_R01LotList[rowIndex],
			insertRow: insertPRD3010_R01LotList,
			defaultRowData: defaultPRD3010_R01LotRow,
			setClickable
		});
	}, [PRD3010_R01LotList, insertPRD3010_R01LotList, defaultPRD3010_R01LotRow, setClickable]);

	const baseColumnDefs: ColDef<PRD3010_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PRD3010_R01InspectDto) => renderIndexCell(
				params, 
				(innerParams: PRD3010_R01LotDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedPRD3010_R01LotList,
						selectedList: selectedPRD3010_R01LotList,
						dataList: PRD3010_R01LotList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					lotGridRef,
					PRD3010_R01LotList, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removePRD3010_R01LotList, 
					(valueIndex) => addDeletedPRD3010_R01LotList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '생산량', colId: 'lotQty', field: 'lotQty', width: 65,
			editable: (params: any) => params.data.inserted === true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
        {
            headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: false,
		},
		// ...IndexParameterCell(),
	], [deletedPRD3010_R01LotList, PRD3010_R01LotList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PRD3010_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3010_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3010_R01LotList,
		originalDataList: originalPRD3010_R01LotList,
		updateRow: updatePRD3010_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3010_R01LotList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3010_R01LotList,
		deletedList: deletedPRD3010_R01LotList,
		addSelected: addSelectedPRD3010_R01LotList,
		removeSelected: removeSelectedPRD3010_R01LotList,
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3010_R01LotList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3010_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3010_R01LotList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3010_R01LotDto>[] = useMemo(() => [
	], [editPRD3010_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3010_R01LotList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3010_R01LotDto>) => {
		postSortHandler(params, PRD3010_R01LotList, originalPRD3010_R01LotList, setPRD3010_R01LotList);
	}, [PRD3010_R01LotList, originalPRD3010_R01LotList, ]);

	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = PRD3010_R01LotList
			.reduce((sum, item) => sum + (deletedPRD3010_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0);

		return [{
			lotNo: PRD3010_R01LotList.length.toLocaleString() + "건",
			lotQty: totalQty,
		}];	  
	}, [PRD3010_R01LotList, deletedPRD3010_R01LotList,]);

	return (
		<>
			<div className="flex flex-1 gap-[4px] h-full min-h-full">
				<GridContextWrapper<PRD3010_R01LotDto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={lotGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setPRD3010_R01LotList}
					initSelected={initSelectedPRD3010_R01LotList}
					defaultRowData={defaultPRD3010_R01LotRow}
					baseColumnDefs={lotColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setLotColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					
					<SmallTitleSpan>
						생산 Lot
						<button 
							className={`relative w-[150px] bg-blue-dark text-white py-[1px] rounded-[4px] h-[20px] float-right`}
							onClick={() => setModalVisible(!modalVisible)}
							>
							불량내역
						</button>
					</SmallTitleSpan>	
					
					<Grid_Excel<PRD3010_R01LotDto>
						ref={lotGridRef}
						rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
						columnDefs={columnDefs}		
						defaultColDef={cellCssDefaultColDef}

						rowClassRules={createRowClassRules<PRD3010_R01LotDto>({
							idKey: 'valueIndex',
							selectedList: selectedPRD3010_R01LotList,
							deletedList: deletedPRD3010_R01LotList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setLotColumnState}		//컬럼 상태 저장
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={lotScrollTop}
						setScrollTop={setLotScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}	
